/**
 * Identity passthrough — pushes the host page's partner identity token (from
 * widget.js `data-identity-token` / `ProdagiWidget.identify`) to the backend for
 * one surface's sessions. Chat and voice each own one instance, because voice
 * mints its OWN session id and the backend keys verified identity by session id.
 *
 * 🚨 The token is a bearer credential. Nothing here logs, persists, or renders
 * it — and errors are swallowed WITHOUT logging, because an axios error carries
 * the request body (`config.data`), which is the token.
 *
 * Why a serialized queue: `identify(token)` followed quickly by `identify(null)`
 * would otherwise race on the network — if the clear lands before the verify,
 * the session ends up verified AFTER the buyer logged out. Calls therefore run
 * strictly in order, and a call that has been superseded for its session before
 * it was sent is skipped (latest intent per session wins).
 */
import { widgetAPI } from '../services/api';

/** Voice awaits verification before opening its WS; never stall a call longer. */
export const IDENTITY_VERIFY_TIMEOUT_MS = 4000;

export interface IdentitySync {
    /** Verify `token` for `sessionId`, or clear it when `token` is null. Resolves
     *  true only on `verified: true`; never rejects. */
    push: (sessionId: string, agentId: string, token: string | null) => Promise<boolean>;
    /** Clear every session this instance ever sent a token for (host logout). */
    clearAll: () => void;
}

export function createIdentitySync(): IdentitySync {
    let chain: Promise<unknown> = Promise.resolve();
    let seq = 0;
    const latest = new Map<string, number>();
    // Sessions a token was sent for — the only ones a clear has to reach.
    const sent = new Set<string>();

    const push = (sessionId: string, agentId: string, token: string | null) => {
        const mine = ++seq;
        latest.set(sessionId, mine);
        const run = async (): Promise<boolean> => {
            if (latest.get(sessionId) !== mine) return false;
            try {
                if (token) {
                    sent.add(sessionId);
                    const { data } = await widgetAPI.verifyIdentity(sessionId, agentId, token);
                    return data.verified === true;
                }
                if (!sent.has(sessionId)) return false;
                await widgetAPI.clearIdentity(sessionId);
                sent.delete(sessionId);
                return false;
            } catch {
                // Deliberately silent — see the file header. A failed verify
                // just leaves the session unverified; the agent asks the buyer
                // to sign in.
                return false;
            }
        };
        const result = chain.then(run, run);
        chain = result;
        return result;
    };

    const clearAll = () => {
        Array.from(sent).forEach((sessionId) => { void push(sessionId, '', null); });
    };

    return { push, clearAll };
}

/** Resolves with the promise's value, or `fallback` after `ms` — whichever is first. */
export function withTimeout<T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> {
    return new Promise<T>((resolve) => {
        const timer = setTimeout(() => resolve(fallback), ms);
        promise.then(
            (value) => { clearTimeout(timer); resolve(value); },
            () => { clearTimeout(timer); resolve(fallback); },
        );
    });
}
